import { Role, ShiftStatus } from '@caresend/types';
import { nullishFilter, hasStatus, HOUR } from '@caresend/utils';
import { ref, computed } from 'vue';
import { ButtonStyle } from '../../../../components/buttons/ButtonComponent/model.js';
import { getFirstStep } from '../../../../components/specific/portal/components/Portal/components/Shifts/components/ShiftCancelation/model.js';
import { usePortalStore } from '../../../../components/specific/portal/store/portal/index.js';
import { PortalRouteName } from '../model.js';
import { getStore } from '../../../../store/helpers/initStore.js';

var ReloadRouteDataSource;
(function (ReloadRouteDataSource) {
    ReloadRouteDataSource["SHIFTS"] = "shifts";
    ReloadRouteDataSource["USERS"] = "users";
})(ReloadRouteDataSource || (ReloadRouteDataSource = {}));
const reloadFunctions = ref({
    [ReloadRouteDataSource.SHIFTS]: new Map(),
    [ReloadRouteDataSource.USERS]: new Map(),
});
const augmentReload = (key, newReload, dataSource) => {
    reloadFunctions.value[dataSource].set(key, newReload);
};
const executeReloads = (dataSource) => {
    reloadFunctions.value[dataSource].forEach((fn) => fn());
};
const shiftDetailsHeadingValues = (headingParams) => computed(() => {
    var _a;
    const store = getStore();
    const portalStore = usePortalStore();
    // PARAMS & SHIFT
    const { params, query } = headingParams.route;
    const { shiftID } = params;
    const { openCancelPane } = query;
    const prevRouteName = headingParams.route.params
        .backRouteName;
    const shift = shiftID ? portalStore.shifts.get(shiftID) : null;
    const title = shift === null || shift === void 0 ? void 0 : shift.computed.detailsHeaderTitle;
    // ITINERARIES
    const itineraryIDs = computed(() => { var _a; return Object.keys((_a = shift === null || shift === void 0 ? void 0 : shift.itineraries) !== null && _a !== void 0 ? _a : {}); });
    const itineraries = computed(() => itineraryIDs.value.reduce((acc, itineraryID) => {
        const itinerary = portalStore.itineraries.get(itineraryID);
        if (itinerary)
            acc[itineraryID] = itinerary;
        return acc;
    }, {}));
    // DRAFT WAYPOINT IDS
    const waypointIDs = Object.values(itineraries.value).map((itinerary) => itinerary.waypoints.map((waypoint) => waypoint.id)).flat();
    const waypoints = waypointIDs.map((waypointID) => portalStore.waypoints.get(waypointID)).filter(nullishFilter);
    const draftWaypointIDs = computed(() => waypoints.reduce((acc, waypoint) => {
        if (waypoint === null || waypoint === void 0 ? void 0 : waypoint.draftWaypoints) {
            acc.push(...Object.keys(waypoint.draftWaypoints));
        }
        return acc;
    }, []));
    // SHIFT CANCELATION
    const isNurse = computed(() => { var _a; return ((_a = store.state.auth.user) === null || _a === void 0 ? void 0 : _a.role) === Role.NURSE; });
    const isCancelableShiftStatus = computed(() => hasStatus(shift, [
        ShiftStatus.ACCEPTED,
        ShiftStatus.IN_PROGRESS,
    ]));
    const hoursBeforeJob = computed(() => {
        if (!(shift === null || shift === void 0 ? void 0 : shift.startTime.timestamp))
            return undefined;
        return (shift.startTime.timestamp - Date.now()) / HOUR;
    });
    const shiftWasAssignedToOtherNurse = computed(() => { var _a; return Boolean(hasStatus(shift, ShiftStatus.ACCEPTED) && (shift === null || shift === void 0 ? void 0 : shift.userID) !== ((_a = store.state.auth.user) === null || _a === void 0 ? void 0 : _a.id)); });
    const displayShiftCancelation = computed(() => Boolean(isCancelableShiftStatus.value
        && hoursBeforeJob.value
        && isNurse.value
        && !shiftWasAssignedToOtherNurse.value));
    const firstStep = computed(() => {
        if (!hoursBeforeJob.value)
            return undefined;
        return getFirstStep(draftWaypointIDs.value, hoursBeforeJob.value);
    });
    return {
        title,
        backLocation: {
            name: prevRouteName !== null && prevRouteName !== void 0 ? prevRouteName : PortalRouteName.JOBS,
        },
        backButtonStyle: ButtonStyle.FROSTED,
        ...(portalStore.viewAs.nurse ? {} : {
            confirmIcon: 'sync-alt',
            confirmIconActiveAnimation: 'rotate',
            confirmButtonStyle: ButtonStyle.FROSTED,
            confirmEvent: () => executeReloads(ReloadRouteDataSource.USERS),
        }),
        overflowVisible: true,
        tertiaryComponent: displayShiftCancelation.value ? () => import('../../../../components/specific/portal/components/Portal/components/Shifts/components/ShiftCancelation/ShiftCancelation.vue.js')
            : undefined,
        tertiaryComponentProps: displayShiftCancelation.value && shift
            ? {
                shift,
                firstDraftWaypointID: draftWaypointIDs.value[0],
                firstStep: firstStep.value,
                startWithPaneOpen: openCancelPane === 'true',
                isDryRun: (_a = shift.debug) === null || _a === void 0 ? void 0 : _a.dryRun,
            }
            : undefined,
    };
    // Above type-cast is for vite/vue-tsc
});
const shiftsListHeadingValues = (_headingParams) => computed(() => ({
    title: 'Jobs',
    backButtonStyle: ButtonStyle.FROSTED,
    confirmIcon: 'sync-alt',
    confirmIconActiveAnimation: 'rotate',
    confirmButtonStyle: ButtonStyle.FROSTED,
    confirmEvent: () => executeReloads(ReloadRouteDataSource.SHIFTS),
    secondaryComponent: 'PortalMapViewToggle',
}));

export { ReloadRouteDataSource, augmentReload, executeReloads, shiftDetailsHeadingValues, shiftsListHeadingValues };
